import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { GradePill } from './VendorsPage'
import { appVendorPerformanceQuery, contractText, hours, pct, vendorsUrl, type VendorFigures } from './vendorsApi'
import './vendors.css'

/**
 * "Vendor performance" pe fisa aplicatiei: cum a raspuns furnizorul de suport
 * al aplicatiei - pe aplicatia asta singura, si pe toate aplicatiile lui pe
 * care le vede omul - langa timpul din contract. Legatura duce la pagina
 * furnizorilor, deschisa la el.
 *
 * Fara drept, inainte de migrare sau pentru o aplicatie care nu se vede,
 * cardul nu apare.
 */
export function VendorPerformanceCard({ appId }: { appId: number }) {
  const [days, setDays] = useState(90)
  const card = useQuery({ ...appVendorPerformanceQuery(appId, days), retry: false })
  const data = card.data
  if (!data?.ready || !data.app) return null
  const vendor = data.vendor ?? null
  const name = data.vendorName ?? ''

  return (
    <>
      <div className="adm-section">
        <h4>Vendor performance</h4>
        <span className="adm-section-note">{name} - from real vendor cases, against the contract</span>
        <select
          id="vpDays"
          aria-label="Period"
          className="form-control input-sm"
          style={{ marginLeft: 'auto', width: 'auto' }}
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          {(data.periods ?? [30, 90, 365]).map((d) => (
            <option key={d} value={d}>
              {d === 365 ? 'last 12 months' : `last ${d} days`}
            </option>
          ))}
        </select>
      </div>
      <div className="adm-card" id="appVendorPerformance">
        {vendor === null || vendor.cases === 0 ? (
          <p className="text-muted" style={{ padding: '10px 15px' }} id="vpEmpty">
            No vendor cases in this period, so there is nothing to rate yet.
          </p>
        ) : (
          <table className="table adm-list">
            <thead>
              <tr>
                <th></th>
                <th>Cases</th>
                <th>First response (median)</th>
                <th>Contract</th>
                <th>Resolution (avg)</th>
                <th>Deadline missed</th>
                <th>Level 2+</th>
                <th>Grade</th>
              </tr>
            </thead>
            <tbody>
              <Line label="This application" v={data.app} />
              <Line label={`All ${vendor.appCount} application${vendor.appCount === 1 ? '' : 's'} of ${name}`} v={vendor} />
            </tbody>
          </table>
        )}
        <div className="vp-foot">
          <span className="adm-muted small">Clock hours from the escalation; the deadline is the case due time, in support hours.</span>
          <a href={vendorsUrl(data.vendorKey, days)} id="vpAll" target="_blank" rel="noopener noreferrer">
            All vendors <i className="fas fa-arrow-right"></i>
          </a>
        </div>
      </div>
    </>
  )
}

function Line({ label, v }: { label: string; v: VendorFigures }) {
  const slower = v.responseVsContract !== null && v.responseVsContract > 1
  return (
    <tr>
      <td>{label}</td>
      <td>{v.cases}</td>
      <td className={`adm-nowrap${slower ? ' text-danger' : ''}`}>{hours(v.medianFirstResponseHours)}</td>
      <td className="adm-nowrap">{contractText(v)}</td>
      <td className="adm-nowrap">{hours(v.avgResolutionHours)}</td>
      <td className="adm-nowrap">{pct(v.breachedPct)}</td>
      <td>{v.escalatedL2}</td>
      <td>
        <GradePill grade={v.grade} />
      </td>
    </tr>
  )
}
